import React, { useState } from 'react';

const FoodCategorySelector = ({ selectedCategories = [], onChange }) => {
  const [customFood, setCustomFood] = useState('');
  const [isAddingCustom, setIsAddingCustom] = useState(false);

  // Premium food categories with icons and colors
  const foodCategories = [
    { id: 'dairy', name: 'Dairy', icon: '🥛', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    { id: 'gluten', name: 'Gluten', icon: '🍞', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    { id: 'vegetable', name: 'Vegetables', icon: '🥬', color: 'bg-green-100 text-green-700 border-green-200' },
    { id: 'meat', name: 'Meat', icon: '🥩', color: 'bg-red-100 text-red-700 border-red-200' },
    { id: 'fruit', name: 'Fruits', icon: '🍎', color: 'bg-pink-100 text-pink-700 border-pink-200' },
    { id: 'seafood', name: 'Seafood', icon: '🐟', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    { id: 'nuts', name: 'Nuts & Seeds', icon: '🥜', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    { id: 'spicy', name: 'Spicy', icon: '🌶️', color: 'bg-red-100 text-red-700 border-red-200' },
    { id: 'drink', name: 'Beverages', icon: '☕', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    { id: 'snack', name: 'Snacks', icon: '🍪', color: 'bg-purple-100 text-purple-700 border-purple-200' },
    { id: 'processed', name: 'Processed', icon: '📦', color: 'bg-gray-100 text-gray-700 border-gray-200' },
    { id: 'sweet', name: 'Sweets', icon: '🍭', color: 'bg-pink-100 text-pink-700 border-pink-200' },
  ];

  const handleCategoryToggle = (categoryId) => {
    const isSelected = selectedCategories.includes(categoryId);
    if (isSelected) {
      onChange(selectedCategories.filter(id => id !== categoryId));
    } else {
      onChange([...selectedCategories, categoryId]);
    }
  };

  const handleCustomFoodAdd = () => {
    if (customFood.trim()) {
      // Add custom food as a special category
      const customId = `custom_${Date.now()}`;
      onChange([...selectedCategories, customId]);
      setCustomFood('');
      setIsAddingCustom(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCustomFoodAdd();
    } else if (e.key === 'Escape') {
      setIsAddingCustom(false);
      setCustomFood('');
    }
  };

  const getCustomFoodName = (categoryId) => {
    if (categoryId.startsWith('custom_')) {
      return customFood || 'Custom Food';
    }
    return foodCategories.find(cat => cat.id === categoryId)?.name || categoryId;
  };

  return (
    <div className="space-y-4">
      {/* Instructions */}
      <div className="text-center">
        <p className="text-sm text-neutral-600 font-medium">
          Select food categories that describe your meal
        </p>
        <p className="text-xs text-neutral-500 mt-1">
          Tap multiple categories to build your meal profile
        </p>
      </div>

      {/* Category Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {foodCategories.map((category) => {
          const isSelected = selectedCategories.includes(category.id);
          return (
            <button
              key={category.id}
              onClick={() => handleCategoryToggle(category.id)}
              className={`
                food-chip group relative overflow-hidden transition-all duration-300 p-3 min-h-[4rem]
                ${isSelected 
                  ? 'food-chip-selected scale-105 shadow-premium' 
                  : 'hover:scale-105 hover:shadow-glass'
                }
              `}
            >
              {/* Background gradient for selected items */}
              {isSelected && (
                <div className="absolute inset-0 bg-primary-gradient opacity-90"></div>
              )}
              
              {/* Content */}
              <div className="relative z-10 flex flex-col items-center space-y-1">
                <span className="text-2xl group-hover:scale-110 transition-transform duration-300">
                  {category.icon}
                </span>
                <span className={`text-xs font-medium text-center leading-tight ${
                  isSelected ? 'text-white' : 'text-neutral-700'
                }`}>
                  {category.name}
                </span>
              </div>
              
              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute top-1 right-1 w-4 h-4 bg-white/30 rounded-full flex items-center justify-center">
                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
              
              {/* Shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 opacity-0 group-hover:opacity-100 group-hover:translate-x-full transition-all duration-700"></div>
            </button>
          );
        })}

        {/* Add Custom Food Button */}
        <button
          onClick={() => setIsAddingCustom(true)}
          className="food-chip group relative overflow-hidden transition-all duration-300 p-3 min-h-[4rem] border-2 border-dashed border-neutral-300 hover:border-primary-400"
        >
          <div className="relative z-10 flex flex-col items-center space-y-1">
            <span className="text-2xl group-hover:scale-110 transition-transform duration-300">
              ➕
            </span>
            <span className="text-xs font-medium text-center leading-tight text-neutral-600">
              Add Custom
            </span>
          </div>
        </button>
      </div>

      {/* Custom Food Input Modal */}
      {isAddingCustom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20 backdrop-blur-sm">
          <div className="glass-card-strong p-6 w-full max-w-sm scale-in">
            <h3 className="text-lg font-semibold text-neutral-800 mb-4 text-center">
              Add Custom Food
            </h3>
            <div className="space-y-4">
              <input
                type="text"
                value={customFood}
                onChange={(e) => setCustomFood(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Enter food name..."
                className="input-glass text-center"
                autoFocus
              />
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setIsAddingCustom(false);
                    setCustomFood('');
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCustomFoodAdd}
                  disabled={!customFood.trim()}
                  className="btn-primary flex-1"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selected Categories Display */}
      {selectedCategories.length > 0 && (
        <div className="glass-card p-4 space-y-3">
          <h4 className="text-sm font-semibold text-neutral-700 text-center">
            Selected Food Categories ({selectedCategories.length})
          </h4>
          <div className="flex flex-wrap gap-2 justify-center">
            {selectedCategories.map((categoryId) => {
              const category = foodCategories.find(cat => cat.id === categoryId);
              const isCustom = categoryId.startsWith('custom_');
              
              return (
                <div
                  key={categoryId}
                  className="inline-flex items-center space-x-2 px-3 py-1 bg-primary-gradient text-white rounded-full text-sm font-medium shadow-premium"
                >
                  <span>{isCustom ? '📝' : category?.icon}</span>
                  <span>{isCustom ? getCustomFoodName(categoryId) : category?.name}</span>
                  <button
                    onClick={() => handleCategoryToggle(categoryId)}
                    className="food-chip-remove w-4 h-4 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors duration-200"
                  >
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
          
          {/* Clear all button */}
          <div className="text-center">
            <button
              onClick={() => onChange([])}
              className="text-xs text-neutral-500 hover:text-neutral-700 underline transition-colors duration-200"
            >
              Clear all selections
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoodCategorySelector;